import * as React from 'react';
import { useDispatch } from 'react-redux';
import { tryUpdateRelease, UpdateSelectedBuildRequest } from '../../actions/release';
import { ReleaseUpdatable } from '../../actions/updatable';
import { AppPlatform, AppStoreAvailableBuild, AppStoreType, RunwayResponseType } from '../../types';
import {
    displayBuildTypeForPlatformAndAppStoreType,
    displayStoreNameForAppStoreType,
} from '../../helpers/displayHelpers';
import { Nullable, ReleaseComponentProps, StepRunComponentProps } from '../../typescriptHelpers';
import { globalMessage } from '../../utils/globalMessage';
import ChangeSelectedBuildModalBase from '@/components/selectedAppStoreBuild/ChangeSelectedBuildsModalBase';
import { useCallUpdatable, useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import { StepRunUpdatable } from '@/actions/stepRun';

interface ChangeSelectedBuildModalProps extends ReleaseComponentProps {
    platform: AppPlatform;
    appStoreType: AppStoreType;
    includeAssociatedCIBuilds: boolean;
    open: boolean;
    currentlySelectedAppStoreBuilds?: AppStoreAvailableBuild[];
    availableBuilds: AppStoreAvailableBuild[];
    onClose: () => void;
    onSuccess: () => void;
    stepRunComponentProps: Nullable<StepRunComponentProps>;
}

const ChangeSelectedBuildModal: React.FC<ChangeSelectedBuildModalProps> = (props: ChangeSelectedBuildModalProps) => {
    const dispatch = useDispatch();

    const [
        updateSelectedBuildStepRun,
        updateSelectedBuildIsLoadingStepRun,
        updateSelectedBuildResponseTypeStepRun,
        updateSelectedBuildErrorStepRun,
    ] = useCallUpdatable(StepRunUpdatable.UpdateSelectedBuildsInAppStore, props.stepRunComponentProps);

    const updateSelectedBuildSuccessReleaseStep = useUpdated(ReleaseUpdatable.UpdateSelectedBuild, props.releaseId);
    const updateSelectedBuildIsLoadingReleaseStep = useIsLoading(ReleaseUpdatable.UpdateSelectedBuild, props.releaseId);
    const updateSelectedBuildErrorReleaseStep = useError(ReleaseUpdatable.UpdateSelectedBuild, props.releaseId);

    const updateSelectedBuildSuccess =
        props.stepRunComponentProps ?
            updateSelectedBuildResponseTypeStepRun === RunwayResponseType.Success
        :   updateSelectedBuildSuccessReleaseStep;
    const updateSelectedBuildIsLoading =
        props.stepRunComponentProps ? updateSelectedBuildIsLoadingStepRun : updateSelectedBuildIsLoadingReleaseStep;
    const updateSelectedBuildError =
        props.stepRunComponentProps ? updateSelectedBuildErrorStepRun : updateSelectedBuildErrorReleaseStep;

    const onSaveChanges = (buildIds: string[]) => {
        const request: UpdateSelectedBuildRequest = {
            orgId: props.orgId,
            buildIds: buildIds,
            appId: props.appId,
            releaseId: props.releaseId,
        };

        if (props.stepRunComponentProps) {
            updateSelectedBuildStepRun(request);
        } else {
            dispatch(tryUpdateRelease(ReleaseUpdatable.UpdateSelectedBuild, request, props.releaseId));
        }
    };

    const title = `Change selected ${displayBuildTypeForPlatformAndAppStoreType(
        props.platform,
        props.appStoreType,
    )} in ${displayStoreNameForAppStoreType(props.appStoreType)}`;
    let label = 'Select ';
    switch (props.appStoreType) {
        case AppStoreType.Apple:
            label += 'a build';
            break;
        case AppStoreType.Google:
            label += 'App Bundle / APK';
            break;
        case AppStoreType.Amazon:
            label += 'APK';
            break;
        // intentionally left blank for Huawei as we can't select builds
    }

    React.useEffect(() => {
        if (updateSelectedBuildSuccess) {
            let successMessageForAppStore: string;
            switch (props.appStoreType) {
                case AppStoreType.Apple:
                    successMessageForAppStore = 'Build updated in App Store Connect!';
                    break;
                case AppStoreType.Google:
                    successMessageForAppStore = 'App Bundle / APK updated in the Play Console!';
                    break;
                case AppStoreType.Amazon:
                    successMessageForAppStore = 'APK updated in the Amazon Appstore!';
                    break;
                // intentionally left blank for Huawei as we can't select builds
            }
            globalMessage.success(successMessageForAppStore);

            props.onSuccess();
        }
    }, [updateSelectedBuildSuccess]);

    React.useEffect(() => {
        if (updateSelectedBuildError) {
            globalMessage.error(updateSelectedBuildError);
        }
    }, [updateSelectedBuildError]);

    return (
        <ChangeSelectedBuildModalBase
            platform={props.platform}
            appStoreType={props.appStoreType}
            title={title}
            label={label}
            isLoading={updateSelectedBuildIsLoading}
            includeAssociatedCIBuilds={props.includeAssociatedCIBuilds}
            showRecentBuilds={true}
            showPastBuildsEmptyState={false}
            open={props.open}
            availableBuilds={props.availableBuilds}
            onSaveChanges={onSaveChanges}
            onClose={props.onClose}
            onSuccess={props.onSuccess}
            releaseId={props.releaseId}
            appId={props.appId}
            orgId={props.orgId}
            currentSelection={props.currentlySelectedAppStoreBuilds}
        />
    );
};

export default ChangeSelectedBuildModal;
